import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SlotPicker } from '../components/SlotPicker'
import { ApiError, api } from '../lib/api'
import type { Booking, Slot, SlotsResponse } from '../lib/types'

/**
 * Slot picker (01 §3.3).
 *
 * Only Andrew's open slots are ever shown — the API computes them from his rules minus
 * blackouts, bookings and the lead time, so this screen has no scheduling logic of its own and
 * cannot drift from his real availability.
 *
 * Losing a race is a normal outcome, not an error: two people can want the same Tuesday. A 409
 * carries the fresh slot list with it, so the grid re-renders in place with an explanation
 * rather than an error page.
 */
export function Schedule() {
  const { id: estimateId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [taken, setTaken] = useState<string | null>(null)
  const [pending, setPending] = useState<string | null>(null)

  // Ask for the whole window rather than accepting the endpoint's 14-day default: the picker
  // now shows how far out Andrew is bookable, and the server clamps the range to his configured
  // horizon anyway, so asking wide returns exactly what is genuinely open and nothing more.
  const { data, isPending, isError } = useQuery({
    queryKey: ['slots'],
    queryFn: () => {
      const to = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
      return api.get<SlotsResponse>(`/availability/slots?to=${encodeURIComponent(to)}`)
    },
    refetchInterval: 60_000,
  })

  const book = useMutation({
    mutationFn: async (slot: Slot) => {
      const held = await api.post<Booking>(
        '/bookings',
        { estimate_id: estimateId, slot_start: slot.start },
        crypto.randomUUID(),
      )
      return api.post<Booking>(`/bookings/${held.id}/confirm`)
    },
    onSuccess: (booking) => navigate(`/estimate/${estimateId}/booked/${booking.id}`),
    onError: async (error) => {
      setPending(null)
      if (error instanceof ApiError && error.status === 409) {
        setTaken('Someone just took that time. Here is what is still open.')
        await queryClient.invalidateQueries({ queryKey: ['slots'] })
      }
    },
  })

  if (isPending) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-12">
        <p className="eyebrow">Step 2 of 3</p>
        <h1 className="mt-4 text-3xl">When suits you?</h1>
        <p className="mt-3 text-muted">Loading Andrew’s calendar…</p>
        {/* A skeleton in the shape of the thing that is coming, so the page does not jump. */}
        <div aria-hidden="true" className="mt-8 grid gap-2 sm:grid-cols-7">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="h-[76px] rounded-card border border-line bg-white/60" />
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-14">
        <h1 className="text-3xl">We could not load the calendar.</h1>
        <p className="mt-4">
          Your request is saved. Reload in a moment, or email{' '}
          <a href="mailto:andrew@asbhomerenovations.com" className="text-blue-deep">
            andrew@asbhomerenovations.com
          </a>
          .
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-12">
      <p className="eyebrow">Step 2 of 3</p>
      <h1 className="mt-4 text-3xl">When suits you?</h1>
      <p className="mt-3 text-muted">
        Visits take about an hour. Times are {data.timezone.replace('_', ' ')}. The soonest Andrew
        can come is {data.lead_time_hours} hours from now.
      </p>

      {taken && (
        <p role="alert" className="mt-6 rounded-card bg-sand px-4 py-3 text-sm text-ink">
          {taken}
        </p>
      )}

      {data.slots.length === 0 ? (
        <p className="mt-8 rounded-card border border-line bg-white p-5">
          Nothing is open in the next couple of weeks. Email{' '}
          <a href="mailto:andrew@asbhomerenovations.com" className="text-blue-deep">
            andrew@asbhomerenovations.com
          </a>{' '}
          and he will find you a time.
        </p>
      ) : (
        <SlotPicker
          slots={data.slots}
          timezone={data.timezone}
          disabled={book.isPending}
          pendingStart={pending}
          onPick={(slot) => {
            setPending(slot.start)
            book.mutate(slot)
          }}
        />
      )}
    </div>
  )
}
